import React, { ChangeEvent, FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import {
  GridCellEditStopParams,
  GridCellEditStopReasons,
  GridEventListener,
  useGridApiContext,
} from '@mui/x-data-grid-pro';
import { useSelector } from 'react-redux';

import { ITimesheetCellDTO } from 'src/api/dtos';
import { PermissionsEnums } from 'src/shared/enums';
import { usePermissions } from 'src/shared/hooks';
import { convertUserInputToDuration, formatInputDuration } from 'src/shared/utils';
import { getDurationFormat } from 'src/store/user/user.selectors';

import { TimesheetCellContextMenu } from './TimesheetCellContextMenu';

interface Props {
  cell: ITimesheetCellDTO;
  isEdited: boolean;
  disabled: boolean;
  onDurationChange: (duration: number) => void;
  onAddExpense: () => void;
  onAddNode: () => void;
}

export const TimesheetDateEditCell: FC<Props> = ({
  cell,
  isEdited,
  disabled,
  onDurationChange,
  onAddExpense,
  onAddNode,
}) => {
  const durationFormat = useSelector(getDurationFormat);
  const hasInputExpense = usePermissions(PermissionsEnums.WTS_INPUT_EXPENSE);

  const inputRef = useRef<HTMLInputElement>(null);
  const apiRef = useGridApiContext();

  const [durationInput, setDurationInput] = useState(
    formatInputDuration(cell.duration, durationFormat, 'seconds', false),
  );

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setDurationInput(event.target.value);
  };

  useEffect(() => {
    const handleCellEditStop: GridEventListener<'cellEditStop'> = ({ reason }: GridCellEditStopParams) => {
      if (reason === GridCellEditStopReasons.escapeKeyDown) {
        return;
      }

      if (!durationInput) {
        onDurationChange(0);
        return;
      }

      const durationInSeconds = convertUserInputToDuration(durationInput, true);

      if (typeof durationInSeconds !== 'number') {
        return;
      }

      onDurationChange(durationInSeconds);
    };

    return apiRef.current.subscribeEvent('cellEditStop', handleCellEditStop, { isFirst: true });
  }, [apiRef, durationInput, onDurationChange]);

  useLayoutEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [inputRef]);

  return (
    <Stack
      direction="row"
      width="100%"
      height="100%"
      justifyContent="space-between"
      alignItems="center"
      position="relative"
      sx={{
        background: (theme) =>
          isEdited
            ? `${theme.palette.mode === 'dark' ? theme.palette.secondary.dark : theme.palette.secondary.main}`
            : 'initial',
      }}
    >
      <TextField
        variant="standard"
        inputRef={inputRef}
        value={durationInput}
        onChange={handleChange}
        InputProps={{
          disableUnderline: true,
        }}
        inputProps={{ sx: { textAlign: 'center' } }}
        sx={{ margin: '0 8px' }}
      />
      <TimesheetCellContextMenu
        cell={cell}
        disabled={disabled}
        hasInputExpense={hasInputExpense}
        onAddExpense={onAddExpense}
        onAddNote={onAddNode}
      />
    </Stack>
  );
};
